import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// ZIFEK CORE — 73. ARCHITECTURE DES TABLES
// Le schéma final suit une logique RELATIONNELLE claire.
// Entités centrales (socle) + tables propres à chaque module.
//   Finance       : invoices, invoice_items, payments…
//   Hospitality   : properties, rooms, bookings…
//   Commerce      : orders, order_items…
// Ce registre décrit l’architecture : entité → table → module → relations.
// ─────────────────────────────────────────────────────────────

export interface SchemaEntity {
  id: number;
  entity_key: string;
  table_name: string | null;
  module: string | null;
  is_core: boolean | null;
  description: string | null;
  columns: string[] | null;
  relations: string[] | null;
  sort_order: number | null;
}

export const MODULE_GROUPS: { key: string; label: string; icon: string; tables: { table: string; label: string }[] }[] = [
  {
    key: 'core', label: 'Zifek Core', icon: 'ri-stack-line',
    tables: [
      { table: 'core_organizations', label: 'Organisations' },
      { table: 'core_memberships', label: 'Membres' },
      { table: 'core_locations', label: 'Établissements' },
      { table: 'core_customers', label: 'Clients' },
      { table: 'core_professionals', label: 'Professionnels' },
    ],
  },
  {
    key: 'finance', label: 'Finance', icon: 'ri-funds-line',
    tables: [
      { table: 'finance_invoices', label: 'Factures (invoices)' },
      { table: 'finance_invoice_items', label: 'Lignes de facture (invoice_items)' },
      { table: 'finance_transactions', label: 'Paiements (payments)' },
      { table: 'finance_expenses', label: 'Dépenses' },
    ],
  },
  {
    key: 'hospitality', label: 'Hospitality', icon: 'ri-hotel-line',
    tables: [
      { table: 'hebergement_properties', label: 'Propriétés (properties)' },
      { table: 'hebergement_units', label: 'Unités / chambres (rooms)' },
      { table: 'hebergement_reservations', label: 'Réservations (bookings)' },
    ],
  },
  {
    key: 'commerce', label: 'Commerce', icon: 'ri-shopping-bag-3-line',
    tables: [
      { table: 'order_headers', label: 'Commandes (orders)' },
      { table: 'order_items', label: 'Lignes de commande (order_items)' },
      { table: 'product_items', label: 'Produits (products)' },
      { table: 'product_variants', label: 'Variantes' },
    ],
  },
];

export async function listSchemaEntities(): Promise<SchemaEntity[]> {
  const { data, error } = await supabase
    .from('core_schema_registry')
    .select('*')
    .order('sort_order', { ascending: true });
  if (error) throw error;
  return (data || []).map((r) => {
    const row = r as SchemaEntity;
    return {
      ...row,
      columns: Array.isArray(row.columns) ? row.columns : [],
      relations: Array.isArray(row.relations) ? row.relations : [],
    };
  });
}

export async function createSchemaEntity(input: {
  entityKey: string;
  tableName: string;
  module?: string;
  isCore?: boolean;
  description?: string | null;
  columns?: string[];
  relations?: string[];
}): Promise<void> {
  const { error } = await supabase.from('core_schema_registry').insert({
    entity_key: input.entityKey,
    table_name: input.tableName,
    module: input.module ?? 'core',
    is_core: input.isCore ?? true,
    description: input.description ?? null,
    columns: input.columns ?? [],
    relations: input.relations ?? [],
    sort_order: 99,
  });
  if (error) throw error;
}

export async function deleteSchemaEntity(id: number): Promise<void> {
  const { error } = await supabase.from('core_schema_registry').delete().eq('id', id);
  if (error) throw error;
}

export interface SchemaStats {
  total: number;
  core: number;
  moduleTables: number;
  modules: number;
}

export async function fetchSchemaStats(): Promise<SchemaStats> {
  const entities = await listSchemaEntities();
  return {
    total: entities.length,
    core: entities.filter((e) => e.is_core).length,
    moduleTables: entities.filter((e) => !e.is_core).length,
    modules: new Set(entities.map((e) => e.module || 'core')).size,
  };
}